import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { resumesAPI, formatApiError } from '../services/api';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import EmptyState from '../components/ui/EmptyState';
import {
  UploadCloud,
  FileText,
  Download,
  Sparkles,
  Calendar,
  Check,
  UserCheck,
} from 'lucide-react';
import './Page.css';

export default function Resumes() {
  const navigate = useNavigate();
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploading, setUploading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showPortfolioModal, setShowPortfolioModal] = useState(false);

  useEffect(() => {
    fetchResumes();
  }, []);

  const fetchResumes = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await resumesAPI.list();
      setResumes(response.data || []);
    } catch (err) {
      setError('Failed to load candidate resumes.');
    } finally {
      setLoading(false);
    }
  };

  const processFile = async (file) => {
    if (!file) return;

    const validTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    const fileExt = file.name.split('.').pop().toLowerCase();

    if (!validTypes.includes(file.type) && !['pdf', 'doc', 'docx'].includes(fileExt)) {
      setError('Only PDF, DOC, or DOCX documents are allowed.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('File size must not exceed 10MB.');
      return;
    }

    try {
      setUploading(true);
      setError('');
      setSuccess('');
      await resumesAPI.upload(file);
      setSuccess('Resume uploaded, text extracted, and CV analysis generated by Groq AI successfully!');
      fetchResumes();
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      const detailStr = typeof detail === 'string' ? detail.toLowerCase() : '';
      if (err.response?.status === 404 || detailStr.includes('profile not found') || detailStr.includes('candidate profile')) {
        setShowPortfolioModal(true);
      } else {
        setError(formatApiError(err, 'Failed to upload and analyze resume.'));
      }
    } finally {
      setUploading(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    processFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const handleDownload = async (resumeId) => {
    setDownloadingId(resumeId);
    setError('');
    try {
      const res = await resumesAPI.download(resumeId);
      const downloadUrl = res.data.download_url;
      if (downloadUrl) {
        window.open(downloadUrl, '_blank');
      } else {
        setError('No signed download URL returned.');
      }
    } catch (err) {
      setError(formatApiError(err, 'Failed to generate signed download URL.'));
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="page-shell">
      {/* Header */}
      <div className="page-header-row">
        <div>
          <h1 className="page-title">Resume Intelligence & Parsing Pool</h1>
          <p className="page-subtitle">
            Upload candidate CVs to trigger automated PyPDF text extraction and Groq AI neural skill profiling.
          </p>
        </div>
      </div>

      {error && <div className="alert alert-error"><span>⚠️ {error}</span></div>}
      {success && <div className="alert alert-success"><span>✓ {success}</span></div>}

      {/* Drag & Drop Upload Zone */}
      <Card
        style={{
          border: isDragOver
            ? '2px dashed var(--brand-500)'
            : '2px dashed var(--border-default)',
          backgroundColor: isDragOver ? 'var(--brand-50)' : 'var(--bg-surface)',
          padding: '48px 24px',
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all var(--transition-fast)',
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: '64px',
              height: '64px',
              borderRadius: 'var(--radius-xl)',
              backgroundColor: isDragOver ? 'var(--brand-100)' : 'var(--bg-surface-subtle)',
              border: '1px solid var(--border-default)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--brand-600)',
            }}
          >
            <UploadCloud size={32} />
          </div>

          <div>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
              {uploading ? 'Processing Resume with Groq AI...' : 'Drag & Drop your Resume here'}
            </h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: '6px' }}>
              Supports PDF, DOC, or DOCX formats up to 10MB.
            </p>
          </div>

          <label style={{ marginTop: '8px' }}>
            <Button
              variant="primary"
              size="md"
              loading={uploading}
              onClick={() => document.getElementById('file-upload-input').click()}
            >
              {uploading ? 'Analyzing CV...' : 'Browse Computer Files'}
            </Button>
            <input
              id="file-upload-input"
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={handleFileUpload}
              disabled={uploading}
              style={{ display: 'none' }}
            />
          </label>
        </div>
      </Card>

      {/* Resumes List */}
      <div style={{ marginTop: '36px' }}>
        <h3 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '6px' }}>
          Parsed Resume Profiles ({resumes.length})
        </h3>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: '16px' }}>
          Securely stored versions accessible for job applications and AI candidate matching.
        </p>

        {loading ? (
          <p style={{ textAlign: 'center', padding: '32px' }}>Loading resumes...</p>
        ) : resumes.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="No resumes uploaded yet"
            description="Upload your first resume above to activate AI talent matching and 1-click job applications."
          />
        ) : (
          <div className="card-grid">
            {resumes.map((r) => (
              <Card key={r.id} hover>
                <Card.Header>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: 'var(--radius-md)',
                          backgroundColor: 'var(--brand-50)',
                          color: 'var(--brand-600)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <FileText size={18} />
                      </div>
                      <div>
                        <Card.Title style={{ fontSize: '0.95rem' }}>
                          {r.file_name || 'Resume Document'}
                        </Card.Title>
                        <Card.Description style={{ fontSize: '0.75rem' }}>
                          {r.file_size ? `${(r.file_size / 1024).toFixed(1)} KB` : 'PDF Document'}
                        </Card.Description>
                      </div>
                    </div>

                    {r.is_primary && (
                      <span className="status-badge badge-success">
                        <Check size={12} /> Primary CV
                      </span>
                    )}
                  </div>
                </Card.Header>

                <Card.Content>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
                    <Calendar size={13} />
                    <span>Uploaded on {new Date(r.created_at).toLocaleDateString()}</span>
                  </div>

                  <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Sparkles size={13} className="text-ai" />
                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--ai-600)' }}>
                      AI Extraction Complete
                    </span>
                  </div>
                </Card.Content>

                <Card.Footer>
                  <Button
                    variant="outline"
                    size="sm"
                    icon={Download}
                    loading={downloadingId === r.id}
                    onClick={() => handleDownload(r.id)}
                    style={{ width: '100%' }}
                  >
                    Download Signed PDF
                  </Button>
                </Card.Footer>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Portfolio Required Modal */}
      <Modal
        isOpen={showPortfolioModal}
        onClose={() => setShowPortfolioModal(false)}
        title="Candidate Portfolio Required"
        subtitle="Complete your profile before uploading your CV"
        maxWidth="480px"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '8px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: 'var(--bg-subtle, rgba(255,255,255,0.03))', padding: '14px', borderRadius: '8px', border: '1px solid var(--border-subtle)' }}>
            <UserCheck size={28} className="text-brand" style={{ flexShrink: 0 }} />
            <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              To enable AI resume parsing and smart job matching, please set up your basic candidate portfolio (headline, contact info, and experience) first.
            </p>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '12px' }}>
            <Button
              variant="secondary"
              onClick={() => setShowPortfolioModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setShowPortfolioModal(false);
                navigate('/candidates');
              }}
            >
              Go to My Portfolio
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
